import { Component, OnInit, EventEmitter, OnDestroy } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { PreferencesService } from './preferences.service';
import { takeUntil } from 'rxjs';
import { UserPreferenceProps, UserPreferences } from '../../../models/data-structures/user-preferences.model';
import {
  OpenProjectDisplayCountOption
} from '../../../models/enums/project/user-preferences/open-project-display-count-option.enum';
import { DropdownDisplayItem } from '../../../models/data-structures/dropdown-display-item';
import {
  OpenProjectSortOption, ProjectSortOptionUtils
} from '../../../models/enums/project/user-preferences/open-project-sort-option.enum';

@Component({
  selector: 'app-preferences-dialog', templateUrl: './preferences-dialog.component.html', styleUrls: ['./preferences-dialog.component.scss']
})
export class PreferencesDialogComponent implements OnInit, OnDestroy {
  onDestroyNotifier = new EventEmitter();

  userPreferences: UserPreferences = new UserPreferences();
  lastSaved: UserPreferences = new UserPreferences();
  unsavedChanges = false;

  UserPreferenceProps = UserPreferenceProps;
  OpenProjectDisplayCountOption = OpenProjectDisplayCountOption;
  openProjDisplayCountOptions: (string | number)[] = [OpenProjectDisplayCountOption.FIVE, OpenProjectDisplayCountOption.TEN, OpenProjectDisplayCountOption.FIFTEEN, OpenProjectDisplayCountOption.TWENTY];

  sortOptions: DropdownDisplayItem[] = [{
    value: OpenProjectSortOption.MOST_RECENT, translationId: ProjectSortOptionUtils.getNameFromProjSortOption(OpenProjectSortOption.MOST_RECENT)
  }, {
    value: OpenProjectSortOption.TITLE, translationId: ProjectSortOptionUtils.getNameFromProjSortOption(OpenProjectSortOption.TITLE)
  }, {
    value: OpenProjectSortOption.START_DATE, translationId: ProjectSortOptionUtils.getNameFromProjSortOption(OpenProjectSortOption.START_DATE)
  }, {
    value: OpenProjectSortOption.END_DATE, translationId: ProjectSortOptionUtils.getNameFromProjSortOption(OpenProjectSortOption.END_DATE)
  },];

  resetBtn = {
    openProjDisplayCount: false, openProjSortOrder: false
  };

  constructor(public dialogRef: MatDialogRef<PreferencesDialogComponent>, private preferencesService: PreferencesService,) {
  }

  ngOnInit(): void {
    this.preferencesService.preferencesUpdatedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe((ready: boolean) => {
        if (ready) {
          this.userPreferences = this.preferencesService.getUserPreferences();
          // only needs to be set once cause the popup is closed, if that ever changes this has to be set on save
          this.lastSaved = UserPreferences.deepCopy(this.userPreferences);
        }
      });

  }

  ngOnDestroy(): void {
    this.onDestroyNotifier.unsubscribe();
  }

  async savePreferences(): Promise<void> {
    await this.preferencesService.saveUserPreferences(this.userPreferences);
    this.dialogRef.close(true);

  }

  closeDialog(): void {
    this.dialogRef.close(false);
  }

  resetInput(property: keyof UserPreferences) {
    const defaultValue = new UserPreferences()[property];
    if (typeof defaultValue === 'number') {
      (this.userPreferences[property] as number) = defaultValue;
    } else {
      this.userPreferences[property] = defaultValue;
    }
    this.checkUnsavedChanges();
  }

  onChange(property: keyof UserPreferences, $event: string): void {
    // set value to new value here because ngModel double binding doesn't
    // update until after ngModelChange execution finishes
    this.userPreferences[property] = $event;
    this.checkUnsavedChanges();
  }

  /**
   * Checks all preferences against the last saved state
   * and updates the unsaved changes boolean based on if
   * changes have been made.
   */
  checkUnsavedChanges() {
    this.unsavedChanges = false;
    for (const prop of Object.values(UserPreferenceProps)) {
      this.unsavedChanges = this.unsavedChanges || (this.userPreferences[prop] !== this.lastSaved[prop]);
    }
  }
}
